import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, History, Send, Wallet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CompactStatCard, DataTable, PageHeader, StatsGrid, type DataTableColumn } from "@/components/shared";
import { useMyWallet, useMyWalletTransactions } from "@/hooks/queries";
import { TransactionCategory, TransactionType } from "@/types/enums";
import type { WalletTransactionResponseDto } from "@/types/wallet.dto";
import { cn } from "@/lib/utils";

const categoryOptions: { value: string; label: string }[] = [
  { value: "all", label: "All categories" },
  { value: String(TransactionCategory.OrderPayment), label: "Order Payment" },
  { value: String(TransactionCategory.OrderRefund), label: "Refund" },
  { value: String(TransactionCategory.WalletTopUp), label: "Top-up" },
  { value: String(TransactionCategory.P2PTransferIn), label: "P2P In" },
  { value: String(TransactionCategory.P2PTransferOut), label: "P2P Out" },
  { value: String(TransactionCategory.SelfCashback), label: "Self Cashback" },
  { value: String(TransactionCategory.LevelCashback), label: "Level Cashback" },
  { value: String(TransactionCategory.RewardCashback), label: "Reward" },
  { value: String(TransactionCategory.Withdrawal), label: "Withdrawal" },
];

const AccountWalletTransactions = () => {
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: wallet, isLoading: walletLoading } = useMyWallet();

  const filter = useMemo(
    () => ({
      type: typeFilter === "all" ? undefined : (Number(typeFilter) as TransactionType),
      category: categoryFilter === "all" ? undefined : (Number(categoryFilter) as TransactionCategory),
      page,
      pageSize,
    }),
    [typeFilter, categoryFilter, page, pageSize],
  );

  const { data, isLoading, isFetching } = useMyWalletTransactions(filter);
  const items = data?.items ?? [];

  const columns: DataTableColumn<WalletTransactionResponseDto>[] = [
    {
      id: "txn",
      header: "Transaction",
      cell: (row) => (
        <div>
          <p className="font-mono text-xs font-medium">{row.transactionId}</p>
          <p className="text-xs text-muted-foreground">{row.categoryText}</p>
        </div>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (row) => (
        <Badge
          variant="outline"
          className={cn(
            "gap-1",
            row.type === TransactionType.Credit
              ? "border-success/30 text-success"
              : "border-destructive/30 text-destructive",
          )}
        >
          {row.type === TransactionType.Credit ? (
            <ArrowUpRight className="h-3 w-3" />
          ) : (
            <ArrowDownRight className="h-3 w-3" />
          )}
          {row.typeText}
        </Badge>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      align: "right",
      cell: (row) => (
        <span
          className={cn(
            "font-semibold",
            row.type === TransactionType.Credit ? "text-success" : "text-destructive",
          )}
        >
          {row.type === TransactionType.Credit ? "+" : "-"}₹{row.amount.toFixed(2)}
        </span>
      ),
    },
    {
      id: "balance",
      header: "Balance after",
      align: "right",
      cell: (row) => <span className="text-sm">₹{row.balanceAfter.toFixed(2)}</span>,
    },
    {
      id: "desc",
      header: "Details",
      cell: (row) => <p className="max-w-[260px] truncate text-xs text-muted-foreground">{row.description}</p>,
    },
    {
      id: "date",
      header: "Date",
      cell: (row) =>
        new Date(row.createdAt).toLocaleString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wallet Transactions"
        description="Full history of wallet payments, cashback, P2P, and top-ups."
      />

      <Card className="bg-gradient-primary text-primary-foreground">
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-primary-foreground/70">Available balance</p>
            <p className="text-3xl font-heading font-bold">
              {walletLoading ? "…" : `₹${(wallet?.availableBalance ?? 0).toFixed(0)}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" asChild>
              <Link to="/account/wallet">
                <Wallet className="mr-1 h-4 w-4" /> Wallet
              </Link>
            </Button>
            <Button variant="secondary" size="sm" asChild>
              <Link to="/account/wallet">
                <Send className="mr-1 h-4 w-4" /> P2P Transfer
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <StatsGrid columns={3}>
        <CompactStatCard title="Total records" value={data?.totalItems ?? 0} icon={History} />
        <CompactStatCard
          title="Lifetime credits"
          value={`₹${(wallet?.totalCredits ?? 0).toFixed(0)}`}
          icon={ArrowUpRight}
        />
        <CompactStatCard
          title="Lifetime debits"
          value={`₹${(wallet?.totalDebits ?? 0).toFixed(0)}`}
          icon={ArrowDownRight}
        />
      </StatsGrid>

      <div className="flex flex-wrap gap-3">
        <Select
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-10 w-[140px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value={String(TransactionType.Credit)}>Credit</SelectItem>
            <SelectItem value={String(TransactionType.Debit)}>Debit</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={categoryFilter}
          onValueChange={(v) => {
            setCategoryFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-10 w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {categoryOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <div className={isFetching ? "opacity-70" : undefined}>
          <DataTable
            columns={columns}
            data={items}
            getRowId={(row) => String(row.id)}
            emptyMessage="No wallet transactions yet. Place an order or receive a transfer to see activity here."
            pagination={{
              page,
              pageSize: data?.pageSize ?? pageSize,
              totalCount: data?.totalItems ?? 0,
              totalPages: data?.totalPages ?? 1,
              from: data?.totalItems ? (page - 1) * (data?.pageSize ?? pageSize) + 1 : 0,
              to: Math.min(page * (data?.pageSize ?? pageSize), data?.totalItems ?? 0),
            }}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AccountWalletTransactions;
